import { useAtom } from "jotai";
import { Loader2, Store } from "lucide-react";
import { useEffect, useState } from "react";

import { useCreateShopifyRecipe, useShopifyStores } from "@design-lab/features/shopify-recipe";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { Button } from "@design-lab/shared/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@design-lab/shared/ui/select";
import { shopifyRecipeLoadingAtom } from "@design-lab/shared/state";

type ShopifyRecipeButtonProps = {
  isAnyLoading: boolean;
  isDesktop: boolean;
};

export function ShopifyRecipeButton({ isAnyLoading, isDesktop }: ShopifyRecipeButtonProps) {
  const { stores, isLoading: isLoadingStores } = useShopifyStores();
  const { createShopifyRecipe, isLoading: isCreatingShopifyRecipe } = useCreateShopifyRecipe();
  const [isShopifyRecipeLoading] = useAtom(shopifyRecipeLoadingAtom);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");
  const validation = useValidation();

  useEffect(() => {
    const firstStore = stores[0];
    if (firstStore && !selectedStoreId) {
      setSelectedStoreId(firstStore.id);
    }
  }, [stores, selectedStoreId]);

  const handleCreateShopifyRecipe = async () => {
    if (!selectedStoreId) return;
    await createShopifyRecipe(selectedStoreId);
  };

  const isDisabled =
    isAnyLoading ||
    isShopifyRecipeLoading ||
    !selectedStoreId ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading;

  return (
    <>
      <Select
        value={selectedStoreId}
        onValueChange={setSelectedStoreId}
        disabled={isAnyLoading || isLoadingStores}
      >
        <SelectTrigger
          data-testid="shopify-store-select"
          className="order-3 h-12 w-40 border-green-600 lg:order-none lg:h-12 xl:h-16"
        >
          <SelectValue placeholder="Select store" />
        </SelectTrigger>
        <SelectContent>
          {stores.map(store => (
            <SelectItem key={store.id} value={store.id} data-testid="shopify-store-option">
              {store.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        data-testid="create-shopify-recipe-button"
        onPress={handleCreateShopifyRecipe}
        variant="outline"
        size={isDesktop ? "3xl" : "xl"}
        className="order-4 w-full border border-green-600 text-green-600 hover:bg-green-50 lg:order-none lg:h-12 lg:w-auto lg:min-w-48 xl:h-16"
        isDisabled={isDisabled}
      >
        {isCreatingShopifyRecipe ? (
          <Loader2 className="size-5 animate-spin lg:size-6" />
        ) : (
          <Store className="size-5 lg:size-6" />
        )}
        {isCreatingShopifyRecipe ? "Creating..." : "Create Shopify Recipe"}
      </Button>
    </>
  );
}
